import { Network } from '@capacitor/network'
import { App } from '@capacitor/app'
import { Capacitor, CapacitorHttp } from '@capacitor/core'

// ── Singleton state ─────────────────────────────────────────────
let _initialized = false
let _networkListenerHandle = null
let _appStateListenerHandle = null
let _webOnlineHandler = null
let _webOfflineHandler = null
let _visibilityHandler = null
let _debounceTimer = null
let _probeSequence = 0
let _probeInFlightPromise = null
let _inFlightSeq = null

const DEBOUNCE_ONLINE_MS = 800
const PROBE_TIMEOUT_MS = 2000
const PUBLIC_PROBE_URL = 'https://www.gstatic.com/generate_204'

// ── Helpers ─────────────────────────────────────────────────────
function resolveProbeUrl() {
  if (process.env.VUE_APP_NETWORK_PROBE_URL) {
    return process.env.VUE_APP_NETWORK_PROBE_URL
  }
  return PUBLIC_PROBE_URL
}

function isProbeEnabled() {
  return process.env.VUE_APP_NETWORK_PROBE_ENABLED !== 'false'
}

// ── Vuex Module ─────────────────────────────────────────────────
export default {
  namespaced: true,

  state: {
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    connectionType: 'unknown'
  },

  mutations: {
    set(state, val) {
      state.isOnline = val
    },
    setConnectionType(state, val) {
      state.connectionType = val
    }
  },

  actions: {
    async init({ commit, dispatch }) {
      if (_initialized) return
      _initialized = true

      if (Capacitor.isNativePlatform()) {
        _networkListenerHandle = await Network.addListener(
          'networkStatusChange',
          (status) => {
            commit('setConnectionType', status.connectionType)
            dispatch('checkAndCommit', status.connected)
          }
        )

        _appStateListenerHandle = await App.addListener(
          'appStateChange',
          async ({ isActive }) => {
            if (isActive) {
              const status = await Network.getStatus()
              dispatch('checkAndCommit', status.connected)
            }
          }
        )

        // Initial check — captive portal saat cold start
        const initialStatus = await Network.getStatus()
        commit('setConnectionType', initialStatus.connectionType)
        dispatch('checkAndCommit', initialStatus.connected)

      } else {
        commit('set', navigator.onLine)

        _webOnlineHandler = () => dispatch('checkAndCommit', true)
        _webOfflineHandler = () => dispatch('checkAndCommit', false)
        window.addEventListener('online', _webOnlineHandler)
        window.addEventListener('offline', _webOfflineHandler)

        _visibilityHandler = () => {
          if (document.visibilityState === 'visible') {
            dispatch('checkAndCommit', navigator.onLine)
          }
        }
        document.addEventListener('visibilitychange', _visibilityHandler)
      }
    },

    async probe({ commit, state }) {
      if (!isProbeEnabled()) return state.isOnline

      // Join in-flight HANYA jika sequence masih valid
      if (_probeInFlightPromise && _inFlightSeq === _probeSequence) {
        return _probeInFlightPromise
      }

      const seq = ++_probeSequence
      _inFlightSeq = seq

      _probeInFlightPromise = (async () => {
        try {
          const url = resolveProbeUrl()
          let online = false

          if (Capacitor.isNativePlatform()) {
            const response = await CapacitorHttp.get({
              url,
              connectTimeout: PROBE_TIMEOUT_MS,
              readTimeout: PROBE_TIMEOUT_MS,
            })
            online = (response.status === 204)
          } else {
            online = navigator.onLine
          }

          if (seq !== _probeSequence) return state.isOnline
          commit('set', online)
          return online
        } catch {
          if (seq !== _probeSequence) return state.isOnline
          commit('set', false)
          return false
        } finally {
          if (_inFlightSeq === seq) {
            _probeInFlightPromise = null
            _inFlightSeq = null
          }
        }
      })()

      return _probeInFlightPromise
    },

    async checkAndCommit({ commit, dispatch }, connected) {
      if (!connected) {
        if (_debounceTimer) {
          clearTimeout(_debounceTimer)
          _debounceTimer = null
        }
        _probeSequence++
        commit('set', false)
        return
      }

      if (_debounceTimer) clearTimeout(_debounceTimer)
      _debounceTimer = setTimeout(async () => {
        _debounceTimer = null
        if (isProbeEnabled()) {
          await dispatch('probe')
        } else {
          commit('set', true)
        }
      }, DEBOUNCE_ONLINE_MS)
    },

    /**
     * Cleanup semua listener, timer, dan state module.
     * Disediakan untuk testing dan edge case HMR.
     * Tidak dipanggil di production flow normal — network module
     * hidup selama app berjalan.
     */
    async destroy() {
      if (_networkListenerHandle) {
        await _networkListenerHandle.remove()
        _networkListenerHandle = null
      }
      if (_appStateListenerHandle) {
        await _appStateListenerHandle.remove()
        _appStateListenerHandle = null
      }
      if (_webOnlineHandler) {
        window.removeEventListener('online', _webOnlineHandler)
        _webOnlineHandler = null
      }
      if (_webOfflineHandler) {
        window.removeEventListener('offline', _webOfflineHandler)
        _webOfflineHandler = null
      }
      if (_visibilityHandler) {
        document.removeEventListener('visibilitychange', _visibilityHandler)
        _visibilityHandler = null
      }
      if (_debounceTimer) {
        clearTimeout(_debounceTimer)
        _debounceTimer = null
      }
      _initialized = false
      _probeSequence = 0
      _probeInFlightPromise = null
      _inFlightSeq = null
    }
  }
}
